import {
  Fragment,
  useEffect,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
  type TableOptions,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from 'lucide-react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import { useIsDesktop } from '@/shared/ui/hooks/use-is-desktop'
import { Pagination } from './pagination'

type MobileCardContext = { rank: number }

type DataTableProps<TData> = {
  columns: ColumnDef<TData, unknown>[]
  data: TData[]
  enableSorting?: boolean
  initialSorting?: SortingState
  enablePagination?: boolean
  enableRowNumbers?: boolean
  pageSize?: number
  globalFilter?: string
  columnFilters?: ColumnFiltersState
  onRowClick?: (row: TData) => void
  rowClassName?: (row: TData) => string | undefined
  renderMobileCard?: (row: TData, context: MobileCardContext) => ReactNode
  renderExpandedRow?: (row: TData) => ReactNode
  isRowExpanded?: (row: TData) => boolean
  onToggleRowExpanded?: (row: TData) => void
}

type RowInteractionProps = {
  role?: 'button'
  tabIndex?: number
  onClick?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void
}

function fuzzyFilter(row: { getValue: (columnId: string) => unknown }, columnId: string, filterValue: string): boolean {
  const value = row.getValue(columnId)
  if (typeof value !== 'string') return false
  return value.toLowerCase().includes(filterValue.toLowerCase())
}

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ArrowUp className="size-3.5 text-primary" />
  if (sorted === 'desc') return <ArrowDown className="size-3.5 text-primary" />
  return <ArrowUpDown className="size-3.5 opacity-40" />
}

function DataTable<TData>({
  columns,
  data,
  enableSorting = false,
  initialSorting = [],
  enablePagination = false,
  enableRowNumbers = false,
  pageSize = 10,
  globalFilter,
  columnFilters,
  onRowClick,
  rowClassName,
  renderMobileCard,
  renderExpandedRow,
  isRowExpanded,
  onToggleRowExpanded,
}: DataTableProps<TData>) {
  const isDesktop = useIsDesktop()
  const [sorting, setSorting] = useState<SortingState>(initialSorting)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  })

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [globalFilter, columnFilters])

  const enableFiltering = globalFilter !== undefined || columnFilters !== undefined

  const tableOptions: TableOptions<TData> = {
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {},
  }

  if (enableFiltering) {
    tableOptions.getFilteredRowModel = getFilteredRowModel()
    tableOptions.globalFilterFn = fuzzyFilter
    if (globalFilter !== undefined) {
      tableOptions.state!.globalFilter = globalFilter
    }
    if (columnFilters !== undefined) {
      tableOptions.state!.columnFilters = columnFilters
    }
  }

  if (enableSorting) {
    tableOptions.getSortedRowModel = getSortedRowModel()
    tableOptions.onSortingChange = setSorting
    tableOptions.state!.sorting = sorting
  }

  if (enablePagination) {
    tableOptions.getPaginationRowModel = getPaginationRowModel()
    tableOptions.onPaginationChange = setPagination
    tableOptions.state!.pagination = pagination
  }

  const table = useReactTable(tableOptions)

  const rows = table.getRowModel().rows
  const pageOffset = enablePagination
    ? table.getState().pagination.pageIndex * table.getState().pagination.pageSize
    : 0

  const rowInteractionProps = (row: TData): RowInteractionProps =>
    onRowClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: () => onRowClick(row),
          onKeyDown: (event) => {
            if (event.target !== event.currentTarget) return
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onRowClick(row)
            }
          },
        }
      : {}

  const paginationControl =
    enablePagination && table.getPageCount() > 1 ? (
      <div className="flex justify-end">
        <Pagination
          currentPage={table.getState().pagination.pageIndex + 1}
          totalPages={table.getPageCount()}
          onPageChange={(page) => table.setPageIndex(page - 1)}
        />
      </div>
    ) : null

  if (renderMobileCard && !isDesktop) {
    return (
      <div className="flex flex-col gap-3">
        {rows.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">No results.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((row, visualIndex) => (
              <div
                key={row.id}
                className={cn(
                  'rounded-lg border bg-card p-3',
                  onRowClick &&
                    'cursor-pointer transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  rowClassName?.(row.original),
                )}
                {...rowInteractionProps(row.original)}
              >
                {renderMobileCard(row.original, { rank: pageOffset + visualIndex + 1 })}
              </div>
            ))}
          </div>
        )}
        {paginationControl}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {renderExpandedRow ? <TableHead className="w-10" /> : null}
              {enableRowNumbers ? <TableHead className="w-10">#</TableHead> : null}
              {headerGroup.headers.map((header) => {
                const canSort = enableSorting && header.column.getCanSort()
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      canSort && 'cursor-pointer select-none',
                      header.column.getIsSorted() && 'text-primary',
                      header.column.columnDef.meta?.headerClassName,
                    )}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort ? <SortIcon sorted={header.column.getIsSorted()} /> : null}
                    </div>
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={
                  columns.length +
                  (enableRowNumbers ? 1 : 0) +
                  (renderExpandedRow ? 1 : 0)
                }
                className="h-24 text-center text-muted-foreground"
              >
                No results.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, visualIndex) => {
              const expanded = isRowExpanded?.(row.original) ?? false
              return (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(
                      onRowClick &&
                        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      rowClassName?.(row.original),
                    )}
                    {...rowInteractionProps(row.original)}
                  >
                    {renderExpandedRow ? (
                      <TableCell className="w-10 pr-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-expanded={expanded}
                          aria-label={expanded ? 'Collapse details' : 'Expand details'}
                          onClick={(clickEvent: MouseEvent<HTMLButtonElement>) => {
                            clickEvent.stopPropagation()
                            onToggleRowExpanded?.(row.original)
                          }}
                        >
                          <ChevronRight
                            className={cn(
                              'size-4 transition-transform',
                              expanded && 'rotate-90',
                            )}
                          />
                        </Button>
                      </TableCell>
                    ) : null}
                    {enableRowNumbers ? (
                      <TableCell className="font-mono">{pageOffset + visualIndex + 1}</TableCell>
                    ) : null}
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cell.column.columnDef.meta?.cellClassName}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {renderExpandedRow && expanded ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={
                          columns.length + (enableRowNumbers ? 1 : 0) + 1
                        }
                        className="bg-muted/30 p-0"
                      >
                        {renderExpandedRow(row.original)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              )
            })
          )}
        </TableBody>
      </Table>
      {paginationControl}
    </div>
  )
}

export { DataTable }
export type { DataTableProps, MobileCardContext }
